import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FormSubmissions = () => {
  const { id } = useParams();
  const [form, setForm] = useState(null);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [f, s] = await Promise.all([axios.get(`${API}/cms/forms/${id}`), axios.get(`${API}/cms/forms/${id}/submissions`)]);
      setForm(f.data); setSubs(s.data);
    } catch { toast.error("Failed to load"); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const remove = async (sid) => {
    if (!window.confirm("Delete this submission?")) return;
    await axios.delete(`${API}/cms/forms/${id}/submissions/${sid}`);
    toast.success("Deleted");
    load();
  };

  const downloadCSV = () => {
    if (!form || subs.length === 0) return;
    const keys = form.fields.map((f) => f.key);
    const headers = ["submitted_at", ...keys, "ip"];
    const rows = subs.map((s) => [s.submitted_at, ...keys.map((k) => s.data?.[k] ?? ""), s.ip || ""]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${form.slug}-submissions.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="text-slate-400">Loading…</div>;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to={`/app/website/forms/${id}`} className="text-slate-500 hover:text-slate-900 p-1"><ArrowLeft size={18} /></Link>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Submissions · {form?.name}</h1>
            <p className="text-xs text-slate-500">{subs.length} total</p>
          </div>
        </div>
        <button onClick={downloadCSV} disabled={subs.length === 0} className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-medium px-4 py-2.5 rounded-lg disabled:opacity-50 text-sm"><Download size={14} /> Download CSV</button>
      </header>

      {subs.length === 0 ? (
        <div className="text-slate-400 text-center py-16 border-2 border-dashed border-slate-200 rounded-xl">No submissions yet.</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">When</th>
                {form.fields.map((f) => <th key={f.key} className="text-left px-4 py-3 font-semibold">{f.label}</th>)}
                <th className="text-right px-4 py-3 font-semibold"></th>
              </tr>
            </thead>
            <tbody>
              {subs.map((s) => (
                <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="px-4 py-3 text-slate-500">{new Date(s.submitted_at).toLocaleString()}</td>
                  {form.fields.map((f) => <td key={f.key} className="px-4 py-3 max-w-xs truncate" title={s.data?.[f.key]}>{String(s.data?.[f.key] ?? "—")}</td>)}
                  <td className="px-4 py-3 text-right"><button onClick={() => remove(s.id)} className="text-red-500 hover:bg-red-50 p-1 rounded"><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FormSubmissions;
